/**
 * ProvenanceShield — Compact radial 7-band custody status badge.
 *
 * Renders a heptagonal SVG shield where each segment represents one custody band.
 * Segment color is driven by band state. No letters at sm size; tooltip + aria
 * explain bands. On first render, segments fill with 50ms stagger animation.
 *
 * Blue Steel compliance:
 *   - Zero hardcoded colors (CSS variables only)
 *   - WCAG 2.1 AA: ARIA roles, keyboard nav, focus rings
 *   - 60fps animation (opacity + transform only)
 *   - prefers-reduced-motion respected
 */

import type {
  ChainStatus,
  ProvenanceStatus,
  ProvenanceBandSummary,
  ProvenanceBandState,
  BandName,
} from './provenance-types';
import { PROVENANCE_COPY } from './provenance-types';

const React = (globalThis as any).React;
const { useState, useEffect, useRef, useCallback } = React;

// ============================================================================
// Constants
// ============================================================================

const BAND_ORDER: BandName[] = [
  'principal', 'generator', 'authorization', 'approval',
  'credential', 'executor', 'downstream',
];

const BAND_LABELS: Record<BandName, string> = {
  principal: 'Human Principal',
  generator: 'AI Generator',
  authorization: 'Policy Authorization',
  approval: 'Approval',
  credential: 'Credential Vault',
  executor: 'Executor Service',
  downstream: 'Downstream System',
};

const SIZE_MAP = { sm: 20, md: 32, lg: 48 };

// ============================================================================
// SVG path generation for heptagonal segments
// ============================================================================

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function describeArc(
  cx: number, cy: number, innerR: number, outerR: number,
  startAngle: number, endAngle: number,
): string {
  const gap = 3;
  const s = startAngle + gap / 2;
  const e = endAngle - gap / 2;

  const outerStart = polarToCartesian(cx, cy, outerR, s);
  const outerEnd = polarToCartesian(cx, cy, outerR, e);
  const innerEnd = polarToCartesian(cx, cy, innerR, e);
  const innerStart = polarToCartesian(cx, cy, innerR, s);

  const largeArc = e - s > 180 ? 1 : 0;

  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${outerR} ${outerR} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerEnd.x} ${innerEnd.y}`,
    `A ${innerR} ${innerR} 0 ${largeArc} 0 ${innerStart.x} ${innerStart.y}`,
    'Z',
  ].join(' ');
}

// ============================================================================
// Color resolution
// ============================================================================

function stateToFillVar(state: ProvenanceBandState): string {
  switch (state) {
    case 'verified': return 'var(--provenance-filled, var(--color-success, #22c55e))';
    case 'not_applicable': return 'var(--provenance-satisfied, var(--color-success-muted, #16a34a))';
    case 'pending': return 'var(--provenance-pending, var(--color-warning, #f59e0b))';
    case 'missing': return 'var(--provenance-empty, var(--card-border, rgba(255,255,255,0.08)))';
    case 'broken': return 'var(--provenance-broken, var(--color-danger, #ef4444))';
    case 'redacted': return 'var(--provenance-redacted, var(--color-neutral, #6b7280))';
    case 'unverifiable': return 'var(--provenance-broken, var(--color-danger, #ef4444))';
    default: return 'var(--provenance-empty, var(--card-border, rgba(255,255,255,0.08)))';
  }
}

function statusToRingStyle(status: ChainStatus | ProvenanceStatus | 'loading'): Record<string, string> {
  switch (status) {
    case 'verified':
      return { stroke: 'var(--provenance-filled, var(--color-success, #22c55e))', filter: 'drop-shadow(0 0 4px var(--provenance-ring-glow, var(--color-success, #22c55e)))' };
    case 'partial':
      return { stroke: 'var(--provenance-pending, var(--color-warning, #f59e0b))' };
    case 'pending':
      return { stroke: 'var(--provenance-pending, var(--color-warning, #f59e0b))', strokeDasharray: '4 3' };
    case 'broken':
    case 'unverifiable':
    case 'failed':
      return { stroke: 'var(--provenance-broken, var(--color-danger, #ef4444))' };
    case 'missing':
      return { stroke: 'var(--provenance-empty, var(--card-border, rgba(255,255,255,0.15)))', strokeDasharray: '2 4' };
    case 'restricted':
      return { stroke: 'var(--provenance-redacted, var(--color-neutral, #6b7280))' };
    case 'not_applicable':
      return { stroke: 'var(--provenance-empty, var(--card-border, rgba(255,255,255,0.08)))' };
    case 'loading':
      return { stroke: 'var(--provenance-empty, var(--card-border, rgba(255,255,255,0.15)))' };
    default:
      return { stroke: 'var(--provenance-empty, var(--card-border, rgba(255,255,255,0.15)))' };
  }
}

// ============================================================================
// Props
// ============================================================================

export interface ProvenanceShieldProps {
  bands?: ProvenanceBandSummary[];
  status: ChainStatus | ProvenanceStatus | 'loading';
  size?: 'sm' | 'md' | 'lg';
  onClick?: () => void;
  actionId?: string;
  ariaLabel?: string;
  testId?: string;
}

// ============================================================================
// Component
// ============================================================================

export function ProvenanceShield({
  bands,
  status,
  size = 'md',
  onClick,
  actionId,
  ariaLabel,
  testId = 'provenance-shield',
}: ProvenanceShieldProps) {
  const [animated, setAnimated] = useState(false);
  const prefersReducedMotion = useRef(false);

  useEffect(() => {
    const mq = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)');
    prefersReducedMotion.current = mq?.matches ?? false;
    setAnimated(true);
  }, []);

  const px = SIZE_MAP[size] || SIZE_MAP.md;
  const viewBox = 100;
  const cx = viewBox / 2;
  const cy = viewBox / 2;
  const outerR = 44;
  const innerR = 28;
  const ringR = 47;
  const segAngle = 360 / 7;

  const satisfied = bands
    ? bands.filter(b => b.state === 'verified' || b.state === 'not_applicable').length
    : 0;
  const total = 7;

  const copyEntry = status !== 'loading' && status in PROVENANCE_COPY
    ? PROVENANCE_COPY[status as ProvenanceStatus]
    : null;
  const tooltipText = status === 'loading'
    ? 'Loading provenance...'
    : copyEntry?.tooltip
      || `${satisfied} of ${total} custody bands satisfied${actionId ? ` \u2014 Action ${actionId.slice(0, 8)}` : ''}`;

  const label = ariaLabel ?? tooltipText;

  const isInteractive = Boolean(onClick);
  const role = isInteractive ? 'button' : 'img';

  const handleKeyDown = useCallback((e: any) => {
    if (!onClick) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  }, [onClick]);

  const ringStyle = statusToRingStyle(status);

  const segments = BAND_ORDER.map((bandName, i) => {
    const bandData = bands?.find(b => b.band === bandName);
    const state: ProvenanceBandState = bandData?.state ?? (status === 'loading' ? 'missing' : 'missing');
    const fill = stateToFillVar(state);
    const startAngle = i * segAngle;
    const endAngle = startAngle + segAngle;
    const d = describeArc(cx, cy, innerR, outerR, startAngle, endAngle);

    const delay = prefersReducedMotion.current ? 0 : i * 50;
    const opacity = animated ? 1 : 0;
    const transition = prefersReducedMotion.current ? 'none' : `opacity 200ms ease ${delay}ms, fill 300ms ease`;

    return React.createElement('path', {
      key: bandName,
      d,
      fill,
      opacity,
      style: { transition },
      'data-testid': `${testId}-segment-${bandName}`,
    },
      React.createElement('title', null, BAND_LABELS[bandName]),
    );
  });

  const loadingAnimation = status === 'loading'
    ? React.createElement('circle', {
        cx, cy, r: ringR,
        fill: 'none',
        stroke: 'var(--provenance-pending, var(--color-warning, #f59e0b))',
        strokeWidth: 2,
        strokeDasharray: '30 70',
        style: {
          transformOrigin: 'center',
          animation: prefersReducedMotion.current ? 'none' : 'provenance-spin 1.2s linear infinite',
        },
        'data-testid': `${testId}-loading`,
      })
    : null;

  const svg = React.createElement('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    viewBox: `0 0 ${viewBox} ${viewBox}`,
    width: px,
    height: px,
    role,
    'aria-label': label,
    tabIndex: isInteractive ? 0 : undefined,
    onClick: onClick ?? undefined,
    onKeyDown: isInteractive ? handleKeyDown : undefined,
    'data-testid': testId,
    style: {
      cursor: isInteractive ? 'pointer' : 'default',
      outline: 'none',
      display: 'inline-block',
      verticalAlign: 'middle',
    },
  },
    // Outer ring
    React.createElement('circle', {
      cx, cy, r: ringR,
      fill: 'none',
      strokeWidth: 1.5,
      'data-testid': `${testId}-ring`,
      ...ringStyle,
    }),
    // Segments
    ...segments,
    // Loading spinner
    loadingAnimation,
  );

  return React.createElement('span', {
    style: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
    },
    title: tooltipText,
  },
    svg,
    React.createElement(ProvenanceShieldStyles, null),
  );
}

function ProvenanceShieldStyles() {
  useEffect(() => {
    const id = 'provenance-shield-keyframes';
    if (document.getElementById(id)) return;
    const style = document.createElement('style');
    style.id = id;
    style.textContent = `
      @keyframes provenance-spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      @keyframes provenance-pulse-ring {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }
      [data-testid*="provenance-shield"]:focus-visible {
        outline: 2px solid var(--main-accent, #818cf8);
        outline-offset: 2px;
        border-radius: 50%;
      }
    `;
    document.head.appendChild(style);
  }, []);
  return null;
}

export default ProvenanceShield;
